import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getSystemSummary,
  getSystemPlans,
  createSystemPlan,
  updateSystemPlan,
  deleteSystemPlan,
  getSystemClients,
  createSystemClient,
  updateSystemClient,
  renewSystemClient,
  toggleSystemClientStatus,
  deleteSystemClient,
  getSystemPayments,
  createSystemPayment,
  updateSystemPayment,
  markSystemPaymentPaid,
  chargeSystemPix,
  checkSystemPixStatus,
  deleteSystemPayment,
  getPagSeguroSettings,
  updatePagSeguroSettings,
  testPagSeguroConnection,
} from '../../services/adminService';
import QRCode from 'qrcode';
import './AdminPage.css';
import { toLocalDateInput } from '@sf/shared';

const SYSTEMS_CONFIG = {
  financial: {
    id: 'financial',
    title: 'Sistema Financeiro',
    icon: '💰',
    badgeClass: 'badge-financial',
    color: '#4f46e5',
    tag: 'Web • Porta 3000',
    description: 'Gestão de contas a pagar, contas a receber, fluxo de caixa, cartões e relatórios bancários.',
  },
  fleet: {
    id: 'fleet',
    title: 'Gestão de Frotas',
    icon: '🚚',
    badgeClass: 'badge-fleet',
    color: '#059669',
    tag: 'Módulo • Porta 3002',
    description: 'Controle de viagens, fretes a receber, manutenções, abastecimentos e custos operacionais.',
  },
  task: {
    id: 'task',
    title: 'Gerenciador de Tarefas',
    icon: '📋',
    badgeClass: 'badge-task',
    color: '#d97706',
    tag: 'Kanban • Porta 3004',
    description: 'Quadros visuais de trabalho, cartões de atividades, atribuição de membros e fluxo ágil.',
  },
};

const BILLING_CYCLES = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  semi_annual: 'Semestral',
  annual: 'Anual',
  custom: 'Personalizado',
};

const formatCurrency = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
};

function PixQrCode({ text, fallbackUrl }) {
  const [dataUrl, setDataUrl] = useState('');

  useEffect(() => {
    if (text) {
      QRCode.toDataURL(text, { width: 170, margin: 1 })
        .then(setDataUrl)
        .catch(() => setDataUrl(''));
    }
  }, [text]);

  const src = dataUrl || fallbackUrl;
  if (!src) {
    return (
      <div style={{ width: 170, height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', color: '#64748b', fontSize: '0.85rem' }}>
        Gerando QR Code...
      </div>
    );
  }

  return (
    <img
      src={src}
      alt="QR Code PIX PagSeguro"
      style={{ width: 170, height: 170, display: 'block', borderRadius: 8 }}
    />
  );
}

export default function AdminSystemDetailPage() {
  const { systemId } = useParams();
  const navigate = useNavigate();

  const currentSystem = SYSTEMS_CONFIG[systemId] || SYSTEMS_CONFIG.financial;
  const location = useLocation();
  const queryTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(queryTab || 'plans'); // 'plans' | 'clients' | 'payments' | 'pagseguro'

  // Data states
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // PagSeguro Settings state
  const [pagseguroSettings, setPagseguroSettings] = useState(null);
  const [pagseguroForm, setPagseguroForm] = useState({
    token: '',
    sandbox: true,
    webhook_secret: '',
    apply_to_all_systems: true,
    pix_key: '',
    pix_merchant_name: '',
    pix_merchant_city: '',
  });
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [savingPagseguro, setSavingPagseguro] = useState(false);
  const [testingPagseguro, setTestingPagseguro] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copiedWebhook, setCopiedWebhook] = useState(false);

  // Modals
  const [planModal, setPlanModal] = useState({ open: false, plan: null });
  const [clientModal, setClientModal] = useState({ open: false, subscription: null });
  const [renewModal, setRenewModal] = useState({ open: false, subscription: null });
  const [paymentModal, setPaymentModal] = useState({ open: false, payment: null });
  const [pixModal, setPixModal] = useState({ open: false, payment: null, pixData: null, checking: false, copied: false });

  // Load summary and data for active tab
  const loadSummary = useCallback(() => {
    getSystemSummary(currentSystem.id)
      .then(setSummary)
      .catch(console.error);
  }, [currentSystem.id]);

  const loadPlans = useCallback(() => {
    getSystemPlans(currentSystem.id)
      .then(setPlans)
      .catch(console.error);
  }, [currentSystem.id]);

  const loadClients = useCallback(() => {
    getSystemClients(currentSystem.id)
      .then((data) => {
        setClients(data.subscriptions || []);
        setAllCompanies(data.all_companies || []);
      })
      .catch(console.error);
  }, [currentSystem.id]);

  const loadPayments = useCallback(() => {
    getSystemPayments(currentSystem.id)
      .then(setPayments)
      .catch(console.error);
  }, [currentSystem.id]);

  const loadPagSeguro = useCallback(() => {
    getPagSeguroSettings(currentSystem.id)
      .then((data) => {
        setPagseguroSettings(data);
        setPagseguroForm((prev) => ({
          ...prev,
          sandbox: Boolean(data.sandbox),
          token: '',
          webhook_secret: '',
          pix_key: data.pix_key || '',
          pix_merchant_name: data.pix_merchant_name || '',
          pix_merchant_city: data.pix_merchant_city || '',
        }));
      })
      .catch(console.error);
  }, [currentSystem.id]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadSummary(), loadPlans(), loadClients(), loadPayments(), loadPagSeguro()]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [loadSummary, loadPlans, loadClients, loadPayments, loadPagSeguro]);

  useEffect(() => {
    if (queryTab && ['plans', 'clients', 'payments', 'pagseguro'].includes(queryTab)) {
      setActiveTab(queryTab);
    }
  }, [queryTab]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Flash message helper
  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 5000);
  };

  // ── Plan Handlers ────────────────────────────────────────────────────────
  const handleSavePlan = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const payload = {
      name: formData.get('name'),
      description: formData.get('description'),
      price: parseFloat(formData.get('price')),
      billing_cycle: formData.get('billing_cycle'),
      max_users: formData.get('max_users') ? parseInt(formData.get('max_users')) : null,
      max_boards: formData.get('max_boards') ? parseInt(formData.get('max_boards')) : null,
      is_active: formData.get('is_active') === 'on',
      features: (formData.get('features') || '')
        .split('\n')
        .map((f) => f.trim())
        .filter(Boolean),
    };

    try {
      if (planModal.plan?.id) {
        await updateSystemPlan(currentSystem.id, planModal.plan.id, payload);
        showMsg('Plano atualizado com sucesso!');
      } else {
        await createSystemPlan(currentSystem.id, payload);
        showMsg('Plano criado com sucesso!');
      }
      setPlanModal({ open: false, plan: null });
      loadPlans();
      loadSummary();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Erro ao salvar plano.', 'error');
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Tem certeza que deseja excluir o plano "${plan.name}"?`)) return;
    try {
      await deleteSystemPlan(currentSystem.id, plan.id);
      showMsg('Plano removido com sucesso!');
      loadPlans();
      loadSummary();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Erro ao remover plano.', 'error');
    }
  };

  // ── Client Handlers ──────────────────────────────────────────────────────
  const handleSaveClient = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const accessType = formData.get('access_type');
    const payload = {
      company_id: parseInt(formData.get('company_id')),
      plan_id: formData.get('plan_id') ? parseInt(formData.get('plan_id')) : null,
      access_type: accessType,
      status: formData.get('status'),
      starts_at: formData.get('starts_at') || null,
      expires_at: accessType === 'temporary' ? formData.get('expires_at') || null : null,
      price: formData.get('price') ? parseFloat(formData.get('price')) : null,
      billing_cycle: formData.get('billing_cycle') || 'monthly',
      max_users: formData.get('max_users') ? parseInt(formData.get('max_users')) : null,
      max_boards: formData.get('max_boards') ? parseInt(formData.get('max_boards')) : null,
      notes: formData.get('notes') || null,
      create_first_installment: formData.get('create_first_installment') === 'on',
      installment_due_date: formData.get('installment_due_date') || null,
    };

    try {
      if (clientModal.subscription?.id) {
        await updateSystemClient(currentSystem.id, clientModal.subscription.id, payload);
        showMsg('Liberação de acesso atualizada com sucesso!');
      } else {
        await createSystemClient(currentSystem.id, payload);
        showMsg('Acesso liberado com sucesso para a empresa!');
      }
      setClientModal({ open: false, subscription: null });
      loadClients();
      loadSummary();
      loadPayments();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Erro ao salvar liberação.', 'error');
    }
  };

  const handleRenewClient = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const sub = renewModal.subscription;
    const payload = {
      days_to_add: formData.get('days_to_add') ? parseInt(formData.get('days_to_add')) : null,
      new_expires_at: formData.get('new_expires_at') || null,
      create_installment: formData.get('create_installment') === 'on',
      amount: formData.get('amount') ? parseFloat(formData.get('amount')) : null,
      due_date: formData.get('due_date') || null,
    };

    try {
      await renewSystemClient(currentSystem.id, sub.id, payload);
      showMsg('Prazo de acesso renovado com sucesso!');
      setRenewModal({ open: false, subscription: null });
      loadClients();
      loadSummary();
      loadPayments();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Erro ao renovar acesso.', 'error');
    }
  };

  const handleToggleClientStatus = async (sub) => {
    try {
      const res = await toggleSystemClientStatus(currentSystem.id, sub.id);
      showMsg(res.message);
      loadClients();
      loadSummary();
    } catch (err) {
      showMsg('Erro ao alterar status do cliente.', 'error');
    }
  };

  const handleDeleteClient = async (sub) => {
    if (!window.confirm(`Revogar o acesso de "${sub.company?.name || 'Cliente'}" a este sistema?`)) return;
    try {
      await deleteSystemClient(currentSystem.id, sub.id);
      showMsg('Acesso revogado com sucesso.');
      loadClients();
      loadSummary();
    } catch (err) {
      showMsg('Erro ao revogar acesso.', 'error');
    }
  };

  // ── Payment Handlers ─────────────────────────────────────────────────────
  const handleSavePayment = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const payload = {
      company_id: parseInt(formData.get('company_id')),
      amount: parseFloat(formData.get('amount')),
      due_date: formData.get('due_date'),
      reference_month: formData.get('reference_month'),
      status: formData.get('status') || 'pending',
      notes: formData.get('notes') || null,
    };

    try {
      if (paymentModal.payment?.id) {
        await updateSystemPayment(currentSystem.id, paymentModal.payment.id, payload);
        showMsg('Parcela atualizada com sucesso!');
      } else {
        await createSystemPayment(currentSystem.id, payload);
        showMsg('Parcela registrada com sucesso!');
      }
      setPaymentModal({ open: false, payment: null });
      loadPayments();
      loadSummary();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Erro ao salvar parcela.', 'error');
    }
  };

  const handleMarkPaid = async (payment) => {
    if (!window.confirm(`Confirmar baixa manual no valor de ${formatCurrency(payment.amount)}?`)) return;
    try {
      await markSystemPaymentPaid(currentSystem.id, payment.id);
      showMsg('Parcela marcada como paga!');
      loadPayments();
      loadSummary();
      loadClients();
    } catch (err) {
      showMsg('Erro ao marcar como paga.', 'error');
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm('Excluir esta parcela?')) return;
    try {
      await deleteSystemPayment(currentSystem.id, payment.id);
      showMsg('Parcela excluída com sucesso.');
      loadPayments();
      loadSummary();
    } catch (err) {
      showMsg('Erro ao excluir parcela.', 'error');
    }
  };

  // ── Checkout PIX PagSeguro ───────────────────────────────────────────────
  const handleOpenPixCheckout = async (payment) => {
    const isContingency = payment.pagseguro_charge_id?.startsWith('PIX_BACEN_') || false;
    setPixModal({
      open: true,
      payment,
      pixData: payment.pix_qr_code ? {
        qr_code_url: payment.pix_qr_code,
        copy_paste: payment.pix_copy_paste,
        charge_id: payment.pagseguro_charge_id,
        expiration_date: payment.pix_expiration_date,
        is_contingency: isContingency,
      } : null,
      checking: false,
      copied: false,
    });

    // Se ainda não tem PIX gerado, gera automaticamente
    if (!payment.pix_qr_code) {
      try {
        const res = await chargeSystemPix(currentSystem.id, payment.id, {
          name: payment.company?.contact_name || payment.company?.name,
          email: payment.company?.contact_email,
          cpf: payment.company?.document_number,
        });
        setPixModal((prev) => ({
          ...prev,
          payment: res.payment,
          pixData: res.pix,
        }));
        loadPayments();
      } catch (err) {
        showMsg(err.response?.data?.message || 'Erro ao gerar PIX no PagSeguro.', 'error');
      }
    }
  };

  const handleRegeneratePix = async () => {
    if (!pixModal.payment?.id) return;
    setPixModal((prev) => ({ ...prev, pixData: null }));
    try {
      const res = await chargeSystemPix(currentSystem.id, pixModal.payment.id, {
        name: pixModal.payment.company?.contact_name || pixModal.payment.company?.name,
        email: pixModal.payment.company?.contact_email,
        cpf: pixModal.payment.company?.document_number,
      });
      setPixModal((prev) => ({
        ...prev,
        payment: res.payment,
        pixData: res.pix,
      }));
      showMsg(res.message || 'Código PIX atualizado com sucesso!');
      loadPayments();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Erro ao gerar novo PIX.', 'error');
    }
  };

  const handleCopyPixCode = () => {
    if (pixModal.pixData?.copy_paste) {
      navigator.clipboard.writeText(pixModal.pixData.copy_paste);
      setPixModal((prev) => ({ ...prev, copied: true }));
      setTimeout(() => setPixModal((prev) => ({ ...prev, copied: false })), 3000);
    }
  };

  const handleCheckPixStatus = async () => {
    if (!pixModal.payment?.id) return;
    setPixModal((prev) => ({ ...prev, checking: true }));
    try {
      const res = await checkSystemPixStatus(currentSystem.id, pixModal.payment.id);
      if (res.is_paid) {
        showMsg('🎉 Pagamento PIX confirmado com sucesso!');
        setPixModal((prev) => ({ ...prev, open: false }));
        loadPayments();
        loadSummary();
        loadClients();
      } else if (res.is_contingency) {
        showMsg(res.message || 'Aguardando pagamento na conta bancária.', 'info');
      } else {
        showMsg(`Status no PagSeguro: ${res.pagseguro_status || 'Aguardando Pagamento'}.`, 'info');
      }
    } catch (err) {
      showMsg('Erro ao consultar status no PagSeguro.', 'error');
    } finally {
      setPixModal((prev) => ({ ...prev, checking: false }));
    }
  };

  // ── PagSeguro Settings Handlers ──────────────────────────────────────────
  const handleSavePagSeguro = async (e) => {
    e.preventDefault();
    setSavingPagseguro(true);
    setTestResult(null);
    try {
      const res = await updatePagSeguroSettings({
        token: pagseguroForm.token || null,
        sandbox: Boolean(pagseguroForm.sandbox),
        webhook_secret: pagseguroForm.webhook_secret || null,
        apply_to_all_systems: pagseguroForm.apply_to_all_systems,
        pix_key: pagseguroForm.pix_key || null,
        pix_merchant_name: pagseguroForm.pix_merchant_name || null,
        pix_merchant_city: pagseguroForm.pix_merchant_city || null,
      }, currentSystem.id);
      showMsg(res.message || 'Configurações do PagSeguro salvas com sucesso!');
      setPagseguroSettings(res.settings);
      setPagseguroForm((prev) => ({
        ...prev,
        token: '',
        webhook_secret: '',
        pix_key: res.settings?.pix_key || prev.pix_key,
        pix_merchant_name: res.settings?.pix_merchant_name || prev.pix_merchant_name,
        pix_merchant_city: res.settings?.pix_merchant_city || prev.pix_merchant_city,
      }));
    } catch (err) {
      showMsg(err.response?.data?.message || 'Erro ao salvar credenciais do PagSeguro.', 'error');
    } finally {
      setSavingPagseguro(false);
    }
  };

  const handleTestPagSeguro = async () => {
    setTestingPagseguro(true);
    setTestResult(null);
    try {
      const res = await testPagSeguroConnection({
        token: pagseguroForm.token || undefined,
        sandbox: Boolean(pagseguroForm.sandbox),
      }, currentSystem.id);
      setTestResult({
        success: true,
        message: res.message,
        environment: res.environment,
        baseUrl: res.base_url,
      });
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.message || 'Falha ao testar conexão com o PagSeguro.',
        details: err.response?.data?.details || null,
      });
    } finally {
      setTestingPagseguro(false);
    }
  };

  const handleCopyWebhook = () => {
    const url = pagseguroSettings?.webhook_url || `${window.location.origin}/api/webhooks/pagseguro`;
    navigator.clipboard.writeText(url);
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 2500);
  };

  return (
    <div className="adm-page">
      {/* Top Breadcrumb & Switcher */}
      <div className="adm-header" style={{ marginBottom: '1.25rem' }}>
        <div>
          <div className="adm-breadcrumbs">
            <Link to="/admin">Dashboard</Link>
            <span>/</span>
            <Link to="/admin/systems">Sistemas</Link>
            <span>/</span>
            <span>{currentSystem.title}</span>
          </div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.25rem' }}>
            <span>{currentSystem.icon}</span>
            <span>Gestão Isolada — {currentSystem.title}</span>
          </h1>
          <p className="adm-subtitle">{currentSystem.description}</p>
        </div>

        {/* System Switcher Dropdown / Quick Links */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            className="adm-btn adm-btn-secondary"
            value={currentSystem.id}
            onChange={(e) => navigate(`/admin/systems/${e.target.value}`)}
            style={{ fontWeight: 600, padding: '0.5rem 0.8rem' }}
          >
            <option value="financial">💰 Sistema Financeiro</option>
            <option value="fleet">🚚 Gestão de Frotas</option>
            <option value="task">📋 Gerenciador de Tarefas</option>
          </select>
          <Link to="/admin/systems" className="adm-btn adm-btn-secondary">
            ← Visão Geral
          </Link>
        </div>
      </div>

      {msg && (
        <div
          className={`adm-alert ${msg.type === 'error' ? 'adm-alert-danger' : 'adm-alert-success'}`}
          style={{ marginBottom: '1.25rem' }}
        >
          {msg.text}
        </div>
      )}

      {/* KPI Cards for Selected System */}
      <div className="adm-kpi-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="adm-kpi-card" style={{ borderLeft: `4px solid ${currentSystem.color}` }}>
          <div className="kpi-label">Clientes Liberados</div>
          <div className="kpi-value">{summary?.clients?.active ?? '—'}</div>
          <div className="kpi-sub">
            {summary?.clients?.integral || 0} Integrais • {summary?.clients?.temporary || 0} Temporários
          </div>
        </div>

        <div className="adm-kpi-card">
          <div className="kpi-label">Planos do Sistema</div>
          <div className="kpi-value">{summary?.plans?.active ?? '—'}</div>
          <div className="kpi-sub">{summary?.plans?.total || 0} cadastrados no total</div>
        </div>

        <div className="adm-kpi-card">
          <div className="kpi-label">Receita Recebida</div>
          <div className="kpi-value" style={{ color: '#059669' }}>
            {formatCurrency(summary?.financial?.total_received)}
          </div>
          <div className="kpi-sub">{summary?.financial?.count_paid || 0} parcelas quitadas</div>
        </div>

        <div className="adm-kpi-card">
          <div className="kpi-label">Parcelas a Receber</div>
          <div className="kpi-value" style={{ color: '#d97706' }}>
            {formatCurrency(summary?.financial?.total_pending)}
          </div>
          <div className="kpi-sub">{summary?.financial?.count_pending || 0} pendentes / vencendo</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="adm-tabs" style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.25rem' }}>
        <button
          className={`adm-tab-btn ${activeTab === 'plans' ? 'active' : ''}`}
          onClick={() => setActiveTab('plans')}
          style={{
            padding: '0.6rem 1.2rem',
            fontWeight: 600,
            border: 'none',
            borderBottom: activeTab === 'plans' ? `3px solid ${currentSystem.color}` : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'plans' ? currentSystem.color : '#6b7280',
            cursor: 'pointer',
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
        >
          <span>📋</span> Planos do Sistema ({plans.length})
        </button>

        <button
          className={`adm-tab-btn ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => setActiveTab('clients')}
          style={{
            padding: '0.6rem 1.2rem',
            fontWeight: 600,
            border: 'none',
            borderBottom: activeTab === 'clients' ? `3px solid ${currentSystem.color}` : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'clients' ? currentSystem.color : '#6b7280',
            cursor: 'pointer',
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
        >
          <span>👥</span> Liberação de Clientes ({clients.length})
        </button>

        <button
          className={`adm-tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
          style={{
            padding: '0.6rem 1.2rem',
            fontWeight: 600,
            border: 'none',
            borderBottom: activeTab === 'payments' ? `3px solid ${currentSystem.color}` : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'payments' ? currentSystem.color : '#6b7280',
            cursor: 'pointer',
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
        >
          <span>💳</span> Parcelas & Recebimento ({payments.length})
        </button>

        <button
          className={`adm-tab-btn ${activeTab === 'pagseguro' ? 'active' : ''}`}
          onClick={() => setActiveTab('pagseguro')}
          style={{
            padding: '0.6rem 1.2rem',
            fontWeight: 600,
            border: 'none',
            borderBottom: activeTab === 'pagseguro' ? `3px solid ${currentSystem.color}` : '3px solid transparent',
            background: 'transparent',
            color: activeTab === 'pagseguro' ? currentSystem.color : '#6b7280',
            cursor: 'pointer',
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
        >
          <span>⚙️</span> Gateway PagSeguro (PIX)
          {pagseguroSettings?.has_token ? (
            <span
              style={{
                fontSize: '11px',
                background: pagseguroSettings.sandbox ? '#fef3c7' : '#dcfce7',
                color: pagseguroSettings.sandbox ? '#92400e' : '#166534',
                padding: '2px 7px',
                borderRadius: 9999,
                fontWeight: 700,
              }}
            >
              {pagseguroSettings.sandbox ? 'Sandbox' : 'Produção'}
            </span>
          ) : (
            <span
              style={{
                fontSize: '11px',
                background: '#fee2e2',
                color: '#991b1b',
                padding: '2px 7px',
                borderRadius: 9999,
                fontWeight: 700,
              }}
            >
              Não configurado
            </span>
          )}
        </button>
      </div>

      {loading && <div className="adm-loading" style={{ margin: '2rem 0' }}>Carregando dados do sistema...</div>}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* TAB 1: PLANOS DO SISTEMA                                            */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {activeTab === 'plans' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ margin: 0 }}>Planos de Assinatura — {currentSystem.title}</h3>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                Gerencie valores, ciclos e recursos inclusos em cada plano para este sistema.
              </p>
            </div>
            <button
              className="adm-btn adm-btn-primary"
              onClick={() => setPlanModal({ open: true, plan: null })}
              style={{ background: currentSystem.color, borderColor: currentSystem.color }}
            >
              + Novo Plano
            </button>
          </div>

          {plans.length === 0 ? (
            <div className="adm-empty">
              <div className="adm-empty-icon">📋</div>
              <h4>Nenhum plano cadastrado para este sistema</h4>
              <p>Crie planos com ciclos mensais, trimestrais ou anuais para comercializar o {currentSystem.title}.</p>
              <button
                className="adm-btn adm-btn-primary"
                onClick={() => setPlanModal({ open: true, plan: null })}
                style={{ background: currentSystem.color, borderColor: currentSystem.color, marginTop: '0.5rem' }}
              >
                Criar Primeiro Plano
              </button>
            </div>
          ) : (
            <div className="plans-grid">
              {plans.map((p) => (
                <div key={p.id} className={`plan-card ${!p.is_active ? 'plan-card--inactive' : ''}`}>
                  <div className="plan-card__header">
                    <div>
                      <div className="plan-card__name">{p.name}</div>
                      <span className={`adm-badge ${p.is_active ? 'adm-badge-active' : 'adm-badge-suspended'}`}>
                        {p.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div className="plan-card__price">
                      <div className="plan-card__price-value">{formatCurrency(p.price)}</div>
                      <div className="plan-card__price-cycle">/{BILLING_CYCLES[p.billing_cycle] || p.billing_cycle}</div>
                    </div>
                  </div>

                  {p.description && <p className="plan-card__desc">{p.description}</p>}

                  <div className="plan-card__meta">
                    <strong>Limite de Usuários:</strong> {p.max_users ? `${p.max_users} usuários` : 'Ilimitado'}
                  </div>

                  {currentSystem.id === 'task' && (
                    <div className="plan-card__meta">
                      <strong>Limite de Quadros:</strong> {p.max_boards ? `${p.max_boards} quadros` : 'Ilimitado'}
                    </div>
                  )}

                  {p.features && p.features.length > 0 && (
                    <ul className="plan-card__features">
                      {p.features.map((feat, idx) => (
                        <li key={idx}>✓ {feat}</li>
                      ))}
                    </ul>
                  )}

                  <div className="plan-card__actions">
                    <button
                      className="adm-btn adm-btn-secondary adm-btn-sm"
                      onClick={() => setPlanModal({ open: true, plan: p })}
                    >
                      Editar
                    </button>
                    <button
                      className="adm-btn adm-btn-danger adm-btn-sm"
                      onClick={() => handleDeletePlan(p)}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* TAB 2: LIBERAÇÃO DE CLIENTES                                       */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {activeTab === 'clients' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ margin: 0 }}>Liberação de Clientes — {currentSystem.title}</h3>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                Controle quais empresas possuem permissão de acesso (por tempo determinado ou integral).
              </p>
            </div>
            <button
              className="adm-btn adm-btn-primary"
              onClick={() => setClientModal({ open: true, subscription: null })}
              style={{ background: currentSystem.color, borderColor: currentSystem.color }}
            >
              + Liberar Acesso para Cliente
            </button>
          </div>

          {clients.length === 0 ? (
            <div className="adm-empty">
              <div className="adm-empty-icon">👥</div>
              <h4>Nenhum cliente liberado ainda</h4>
              <p>Clique no botão acima para liberar o acesso de uma empresa a este sistema.</p>
            </div>
          ) : (
            <div className="adm-card" style={{ padding: 0 }}>
              <div className="adm-table-wrapper">
                <table className="adm-table adm-table--cards">
                  <thead>
                    <tr>
                      <th>Cliente / Empresa</th>
                      <th>Plano Vinculado</th>
                      <th>Tipo de Liberação</th>
                      <th>Validade / Expiração</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((sub) => {
                      const isIntegral = sub.access_type === 'integral';
                      const isExpired = !isIntegral && sub.expires_at && new Date(sub.expires_at) < new Date();
                      const daysRemaining = !isIntegral && sub.expires_at
                        ? Math.ceil((new Date(sub.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
                        : null;

                      return (
                        <tr key={sub.id}>
                          <td data-label="Cliente">
                            <div style={{ fontWeight: 600 }}>{sub.company?.name || `Empresa #${sub.company_id}`}</div>
                            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                              CNPJ/CPF: {sub.company?.document_number || '—'}
                            </div>
                          </td>
                          <td data-label="Plano">
                            <div>{sub.plan?.name || sub.plan_name || 'Acesso Avulso'}</div>
                            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                              {formatCurrency(sub.price)} ({BILLING_CYCLES[sub.billing_cycle] || sub.billing_cycle})
                            </div>
                            <div style={{ fontSize: '0.74rem', color: '#4b5563', marginTop: '3px' }}>
                              <span>👥 {sub.max_users ? `${sub.max_users} usuários` : 'Usuários ilimitados'}</span>
                              {currentSystem.id === 'task' && (
                                <span style={{ marginLeft: 6 }}>• 📋 {sub.max_boards ? `${sub.max_boards} quadros` : 'Quadros ilimitados'}</span>
                              )}
                            </div>
                          </td>
                          <td data-label="Tipo">
                            {isIntegral ? (
                              <span className="adm-badge adm-badge-active" style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>
                                ♾️ Integral (Permanente)
                              </span>
                            ) : (
                              <span className="adm-badge" style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                                ⏱️ Tempo Determinado
                              </span>
                            )}
                          </td>
                          <td data-label="Validade">
                            {isIntegral ? (
                              <span style={{ color: '#059669', fontWeight: 600 }}>Vitalício / Sem expiração</span>
                            ) : (
                              <div>
                                <div style={{ fontWeight: isExpired ? 700 : 500, color: isExpired ? '#dc2626' : '#111827' }}>
                                  {formatDate(sub.expires_at)}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: isExpired ? '#dc2626' : '#6b7280' }}>
                                  {isExpired ? '⚠️ Expirado' : daysRemaining !== null ? `${daysRemaining} dias restantes` : ''}
                                </div>
                              </div>
                            )}
                          </td>
                          <td data-label="Status">
                            {sub.status === 'suspended' ? (
                              <span className="adm-badge adm-badge-suspended">Suspenso</span>
                            ) : isExpired ? (
                              <span className="adm-badge adm-badge-danger" style={{ background: '#fee2e2', color: '#991b1b' }}>Expirado</span>
                            ) : (
                              <span className="adm-badge adm-badge-active">Ativo</span>
                            )}
                          </td>
                          <td data-label="Ações">
                            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {!isIntegral && (
                                <button
                                  className="adm-btn adm-btn-success adm-btn-sm"
                                  onClick={() => setRenewModal({ open: true, subscription: sub })}
                                  title="Estender período de acesso"
                                >
                                  🔄 Renovar
                                </button>
                              )}
                              <button
                                className="adm-btn adm-btn-secondary adm-btn-sm"
                                onClick={() => setClientModal({ open: true, subscription: sub })}
                              >
                                Editar
                              </button>
                              <button
                                className={`adm-btn adm-btn-sm ${sub.status === 'active' ? 'adm-btn-secondary' : 'adm-btn-primary'}`}
                                onClick={() => handleToggleClientStatus(sub)}
                              >
                                {sub.status === 'active' ? 'Suspender' : 'Reativar'}
                              </button>
                              <button
                                className="adm-btn adm-btn-danger adm-btn-sm"
                                onClick={() => handleDeleteClient(sub)}
                                title="Revogar liberação"
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* TAB 3: PARCELAS & RECEBIMENTO                                      */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {activeTab === 'payments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ margin: 0 }}>Parcelas e Recebimento — {currentSystem.title}</h3>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                Gestão de mensalidades, faturas de clientes e checkout via PIX com PagSeguro.
              </p>
            </div>
            <button
              className="adm-btn adm-btn-primary"
              onClick={() => setPaymentModal({ open: true, payment: null })}
              style={{ background: currentSystem.color, borderColor: currentSystem.color }}
            >
              + Nova Parcela / Mensalidade
            </button>
          </div>

          {payments.length === 0 ? (
            <div className="adm-empty">
              <div className="adm-empty-icon">💳</div>
              <h4>Nenhuma parcela registrada para este sistema</h4>
              <p>Cadastre uma parcela ou gere cobranças vinculadas às liberações dos clientes.</p>
              <button
                className="adm-btn adm-btn-primary"
                onClick={() => setPaymentModal({ open: true, payment: null })}
                style={{ background: currentSystem.color, borderColor: currentSystem.color, marginTop: '0.5rem' }}
              >
                Registrar Primeira Parcela
              </button>
            </div>
          ) : (
            <div className="adm-card" style={{ padding: 0 }}>
              <div className="adm-table-wrapper">
                <table className="adm-table adm-table--cards">
                  <thead>
                    <tr>
                      <th>Cliente / Empresa</th>
                      <th>Referência</th>
                      <th>Vencimento</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th>Método</th>
                      <th>Pago em</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((pay) => {
                      const isPending = pay.status === 'pending';
                      const isPaid = pay.status === 'paid';
                      const isOverdue = isPending && pay.due_date && new Date(pay.due_date) < new Date();

                      return (
                        <tr key={pay.id}>
                          <td data-label="Cliente">
                            <div style={{ fontWeight: 600 }}>{pay.company?.name || `Empresa #${pay.company_id}`}</div>
                            {pay.notes && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{pay.notes}</div>}
                          </td>
                          <td data-label="Referência" style={{ fontFamily: 'monospace' }}>
                            {pay.reference_month || '—'}
                          </td>
                          <td data-label="Vencimento">
                            <span style={{ color: isOverdue ? '#dc2626' : '#111827', fontWeight: isOverdue ? 700 : 500 }}>
                              {formatDate(pay.due_date)}
                            </span>
                            {isOverdue && <span style={{ fontSize: '0.72rem', color: '#dc2626', display: 'block' }}>Vencida</span>}
                          </td>
                          <td data-label="Valor" style={{ fontWeight: 700 }}>
                            {formatCurrency(pay.amount)}
                          </td>
                          <td data-label="Status">
                            {isPaid ? (
                              <span className="adm-badge adm-badge-active" style={{ background: '#d1fae5', color: '#065f46' }}>✓ Pago</span>
                            ) : isOverdue ? (
                              <span className="adm-badge" style={{ background: '#fee2e2', color: '#991b1b' }}>Vencido</span>
                            ) : (
                              <span className="adm-badge adm-badge-trial" style={{ background: '#fef3c7', color: '#92400e' }}>Pendente</span>
                            )}
                          </td>
                          <td data-label="Método" style={{ textTransform: 'uppercase', fontSize: '0.78rem' }}>
                            {pay.payment_method || 'PIX'}
                          </td>
                          <td data-label="Pago em" style={{ fontSize: '0.8rem' }}>
                            {formatDateTime(pay.paid_at)}
                          </td>
                          <td data-label="Ações">
                            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {isPending && (
                                <>
                                  <button
                                    className="adm-btn adm-btn-primary adm-btn-sm"
                                    onClick={() => handleOpenPixCheckout(pay)}
                                    style={{ background: '#0284c7', borderColor: '#0284c7', display: 'flex', alignItems: 'center', gap: '0.2rem' }}
                                    title="Checkout PIX via PagSeguro"
                                  >
                                    ⚡ PIX PagSeguro
                                  </button>
                                  <button
                                    className="adm-btn adm-btn-success adm-btn-sm"
                                    onClick={() => handleMarkPaid(pay)}
                                    title="Marcar como Pago manualmente"
                                  >
                                    ✓ Baixar
                                  </button>
                                </>
                              )}
                              {isPaid && pay.pix_copy_paste && (
                                <button
                                  className="adm-btn adm-btn-secondary adm-btn-sm"
                                  onClick={() => handleOpenPixCheckout(pay)}
                                >
                                  Ver Comprovante PIX
                                </button>
                              )}
                              <button
                                className="adm-btn adm-btn-danger adm-btn-sm"
                                onClick={() => handleDeletePayment(pay)}
                                title="Excluir parcela"
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* TAB 4: CONFIGURAÇÕES DO GATEWAY PAGSEGURO (PIX)                    */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {activeTab === 'pagseguro' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Status Header Card */}
          <div
            className="adm-card"
            style={{
              borderLeft: pagseguroSettings?.has_token
                ? pagseguroSettings.sandbox
                  ? '4px solid #f59e0b'
                  : '4px solid #10b981'
                : '4px solid #ef4444',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              padding: '1.25rem 1.5rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: pagseguroSettings?.has_token
                    ? pagseguroSettings.sandbox ? '#fef3c7' : '#dcfce7'
                    : '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                }}
              >
                {pagseguroSettings?.has_token ? (pagseguroSettings.sandbox ? '🧪' : '🛡️') : '⚠️'}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>
                    {pagseguroSettings?.has_token
                      ? `Gateway PagSeguro Conectado (${pagseguroSettings.sandbox ? 'Ambiente Sandbox' : 'Ambiente Produção'})`
                      : 'Credenciais do PagSeguro Não Configuradas'}
                  </h3>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 9999,
                      background: pagseguroSettings?.has_token
                        ? pagseguroSettings.sandbox ? '#fef3c7' : '#dcfce7'
                        : '#fee2e2',
                      color: pagseguroSettings?.has_token
                        ? pagseguroSettings.sandbox ? '#92400e' : '#166534'
                        : '#991b1b',
                    }}
                  >
                    {pagseguroSettings?.has_token
                      ? pagseguroSettings.sandbox ? 'TESTES / SANDBOX' : 'PRODUÇÃO ATIVA'
                      : 'MODO SIMULADO'}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                  {pagseguroSettings?.has_token
                    ? `As cobranças PIX e assinaturas do sistema ${currentSystem.title} utilizam a API oficial PagBank via ${pagseguroSettings.base_url}.`
                    : 'Sem credenciais ativas, as cobranças PIX operam em modo simulado/demonstração. Insira seu Token PagSeguro abaixo para ativar recebimentos reais.'}
                </p>
                {pagseguroSettings?.has_token && (
                  <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#4b5563', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <span>Origem da Credencial: <strong>{pagseguroSettings.source === 'system_database' ? `Banco de Dados (Exclusivo ${currentSystem.title})` : pagseguroSettings.source === 'global_database' ? 'Banco de Dados (Global para Todos os Sistemas)' : pagseguroSettings.source === 'env_file' ? 'Arquivo .env' : '—'}</strong></span>
                    {pagseguroSettings.token_masked && <span>Token Ativo: <code style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>{pagseguroSettings.token_masked}</code></span>}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                className="adm-btn adm-btn-secondary"
                onClick={handleTestPagSeguro}
                disabled={testingPagseguro}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {testingPagseguro ? '🔄 Testando Conexão...' : '🔌 Testar Conexão com PagSeguro'}
              </button>
            </div>
          </div>

          {/* Test Connection Result Alert */}
          {testResult && (
            <div
              style={{
                padding: '1rem 1.25rem',
                borderRadius: 8,
                background: testResult.success ? '#f0fdf4' : '#fef2f2',
                border: testResult.success ? '1px solid #bbf7d0' : '1px solid #fecaca',
                color: testResult.success ? '#166534' : '#991b1b',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                fontSize: '0.9rem',
              }}
            >
              <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{testResult.success ? '✅' : '❌'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>
                  {testResult.success ? 'Conexão com PagSeguro Bem-Sucedida!' : 'Falha na Validação do PagSeguro'}
                </div>
                <div style={{ marginTop: 2 }}>{testResult.message}</div>
                {testResult.baseUrl && (
                  <div style={{ marginTop: 4, fontSize: '0.8rem', opacity: 0.9 }}>
                    Endpoint validado: <code>{testResult.baseUrl}</code> ({testResult.environment})
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setTestResult(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700 }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Configuration Form Card */}
          <div className="adm-card">
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🔐</span> Credenciais e Parâmetros da API PagSeguro / PagBank
            </h3>
            <form onSubmit={handleSavePagSeguro}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.25rem' }}>
                
                {/* Ambiente */}
                <div className="adm-form-group" style={{ margin: 0 }}>
                  <label className="adm-label" style={{ fontWeight: 600 }}>
                    Ambiente PagSeguro
                  </label>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        padding: '0.6rem 1rem',
                        borderRadius: 8,
                        border: pagseguroForm.sandbox ? '2px solid #f59e0b' : '1px solid #d1d5db',
                        background: pagseguroForm.sandbox ? '#fef3c7' : '#fff',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    >
                      <input
                        type="radio"
                        name="sandbox_choice"
                        checked={pagseguroForm.sandbox === true}
                        onChange={() => setPagseguroForm({ ...pagseguroForm, sandbox: true })}
                      />
                      <span>🟡 Sandbox (Ambiente de Testes / Desenvolvimento)</span>
                    </label>

                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        padding: '0.6rem 1rem',
                        borderRadius: 8,
                        border: !pagseguroForm.sandbox ? '2px solid #10b981' : '1px solid #d1d5db',
                        background: !pagseguroForm.sandbox ? '#dcfce7' : '#fff',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    >
                      <input
                        type="radio"
                        name="sandbox_choice"
                        checked={pagseguroForm.sandbox === false}
                        onChange={() => setPagseguroForm({ ...pagseguroForm, sandbox: false })}
                      />
                      <span>🟢 Produção (Recebimentos Reais via Conta PagBank)</span>
                    </label>
                  </div>
                  <span className="adm-form-help">
                    Em Produção, as requisições são enviadas para <code>https://api.pagseguro.com</code>. Em Sandbox, para <code>https://sandbox.api.pagseguro.com</code>.
                  </span>
                </div>

                {/* Token Input */}
                <div className="adm-form-group" style={{ margin: 0 }}>
                  <label className="adm-label" style={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Token de Acesso PagSeguro (Bearer Token) <span style={{ color: '#ef4444' }}>*</span></span>
                    {pagseguroSettings?.has_token && (
                      <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 500 }}>
                        ✓ Token configurado ({pagseguroSettings.token_masked})
                      </span>
                    )}
                  </label>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      type={showToken ? 'text' : 'password'}
                      className="adm-input"
                      style={{ paddingRight: '4rem', fontFamily: showToken ? 'monospace' : 'inherit' }}
                      placeholder={pagseguroSettings?.has_token ? 'Deixe em branco para manter o token atual' : 'Ex: 9A8B7C6D5E4F...'}
                      value={pagseguroForm.token}
                      onChange={(e) => setPagseguroForm({ ...pagseguroForm, token: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowToken(!showToken)}
                      style={{
                        position: 'absolute',
                        right: 8,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6b7280',
                        fontSize: '0.85rem',
                        padding: '4px 8px',
                      }}
                    >
                      {showToken ? 'Ocultar' : 'Exibir'}
                    </button>
                  </div>
                  <span className="adm-form-help">
                    Gerado no painel do PagBank em <strong>Vendas &gt; Integrações &gt; Chaves de API</strong> ou <strong>Configurações da Conta</strong>.
                  </span>
                </div>

                {/* Webhook Secret */}
                <div className="adm-form-group" style={{ margin: 0 }}>
                  <label className="adm-label" style={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Segredo do Webhook (Assinatura de Notificação)</span>
                    {pagseguroSettings?.has_webhook_secret && (
                      <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 500 }}>
                        ✓ Segredo configurado ({pagseguroSettings.webhook_secret_masked})
                      </span>
                    )}
                  </label>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      type={showSecret ? 'text' : 'password'}
                      className="adm-input"
                      style={{ paddingRight: '4rem', fontFamily: showSecret ? 'monospace' : 'inherit' }}
                      placeholder={pagseguroSettings?.has_webhook_secret ? 'Deixe em branco para manter o segredo atual' : 'Opcional: chave secreta para validação de assinatura HMAC'}
                      value={pagseguroForm.webhook_secret}
                      onChange={(e) => setPagseguroForm({ ...pagseguroForm, webhook_secret: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      style={{
                        position: 'absolute',
                        right: 8,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6b7280',
                        fontSize: '0.85rem',
                        padding: '4px 8px',
                      }}
                    >
                      {showSecret ? 'Ocultar' : 'Exibir'}
                    </button>
                  </div>
                  <span className="adm-form-help">
                    Utilizado para autenticar as notificações de liquidação de PIX enviadas pelo PagSeguro diretamente ao nosso servidor.
                  </span>
                </div>

                {/* Chave PIX do Beneficiário & Dados do Titular (Padrão Banco Central / Contingência) */}
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>⚡</span>
                    <strong style={{ color: '#1e40af', fontSize: '0.95rem' }}>
                      Dados para Recebimento via PIX (Chave Direta Banco Central)
                    </strong>
                  </div>
                  <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#1e3a8a', lineHeight: 1.45 }}>
                    Utilizado para gerar cobranças com QR Code oficial e código <em>Copia e Cola</em> (BR Code EMV). Em contas no PagBank, a emissão de PIX dinâmico via API pode requerer liberação de <strong>Whitelist</strong> pelo atendimento PagSeguro. Com sua chave cadastrada abaixo, suas cobranças funcionam imediatamente em qualquer aplicativo de banco sem bloqueios!
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.8rem' }}>
                    <div className="adm-form-group" style={{ margin: 0 }}>
                      <label className="adm-label" style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        Chave PIX do Beneficiário <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="adm-input"
                        placeholder="Ex: falure@gmail.com ou 65.810.698/0001-56"
                        value={pagseguroForm.pix_key}
                        onChange={(e) => setPagseguroForm({ ...pagseguroForm, pix_key: e.target.value })}
                      />
                      <span className="adm-form-help">E-mail, CNPJ, CPF, Celular ou chave aleatória EVP.</span>
                    </div>

                    <div className="adm-form-group" style={{ margin: 0 }}>
                      <label className="adm-label" style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        Nome do Titular da Conta <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="adm-input"
                        placeholder="Ex: ALRES TRANSPORTES"
                        value={pagseguroForm.pix_merchant_name}
                        onChange={(e) => setPagseguroForm({ ...pagseguroForm, pix_merchant_name: e.target.value })}
                      />
                      <span className="adm-form-help">Nome exibido ao cliente no app do banco.</span>
                    </div>

                    <div className="adm-form-group" style={{ margin: 0 }}>
                      <label className="adm-label" style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        Cidade do Titular
                      </label>
                      <input
                        type="text"
                        className="adm-input"
                        placeholder="Ex: BELO HORIZONTE"
                        value={pagseguroForm.pix_merchant_city}
                        onChange={(e) => setPagseguroForm({ ...pagseguroForm, pix_merchant_city: e.target.value })}
                      />
                      <span className="adm-form-help">Município onde a conta foi aberta.</span>
                    </div>
                  </div>
                </div>

                {/* Webhook URL Display */}
                <div className="adm-form-group" style={{ margin: 0 }}>
                  <label className="adm-label" style={{ fontWeight: 600 }}>
                    URL do Webhook para Notificações do PagSeguro (Cadastre no PagBank)
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      className="adm-input"
                      readOnly
                      value={pagseguroSettings?.webhook_url || `${window.location.origin}/api/webhooks/pagseguro`}
                      style={{ background: '#f8fafc', color: '#334155', fontFamily: 'monospace', fontWeight: 600 }}
                    />
                    <button
                      type="button"
                      className="adm-btn adm-btn-secondary"
                      onClick={handleCopyWebhook}
                      style={{ whiteSpace: 'nowrap', minWidth: 120 }}
                    >
                      {copiedWebhook ? '✓ Copiado!' : '📋 Copiar URL'}
                    </button>
                  </div>
                  <span className="adm-form-help">
                    Copie esta URL e cadastre nas configurações de Webhooks / Notificações da sua conta PagBank para que os pagamentos PIX sejam baixados automaticamente.
                  </span>
                </div>

                {/* Scope: Global vs Isolated */}
                <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={pagseguroForm.apply_to_all_systems}
                      onChange={(e) => setPagseguroForm({ ...pagseguroForm, apply_to_all_systems: e.target.checked })}
                    />
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>
                      Aplicar estas credenciais para todos os sistemas integrados (Financeiro, Frotas e Tarefas)
                    </span>
                  </label>
                  <p style={{ margin: '4px 0 0 24px', fontSize: '0.8rem', color: '#64748b' }}>
                    Se desmarcado, as credenciais serão salvas exclusivamente para o sistema <strong>{currentSystem.title}</strong>, permitindo receber em contas PagBank distintas por sistema.
                  </p>
                </div>

                {/* Form Actions */}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <button
                    type="button"
                    className="adm-btn adm-btn-secondary"
                    onClick={handleTestPagSeguro}
                    disabled={testingPagseguro}
                  >
                    {testingPagseguro ? 'Testando...' : '🔌 Testar Conexão'}
                  </button>
                  <button
                    type="submit"
                    className="adm-btn adm-btn-primary"
                    disabled={savingPagseguro}
                    style={{ minWidth: 160 }}
                  >
                    {savingPagseguro ? 'Salvando...' : '💾 Salvar Credenciais'}
                  </button>
                </div>

              </div>
            </form>
          </div>

          {/* Step-by-step Guide Card */}
          <div className="adm-card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📘</span> Passo a Passo: Onde Obter Suas Credenciais no PagBank / PagSeguro
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', fontSize: '0.875rem', color: '#475569' }}>
              <div style={{ background: '#fff', padding: '14px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>1. Acesse o Painel PagBank</div>
                <div>
                  Entre em sua conta jurídica ou física no PagBank (<a href="https://pagseguro.uol.com.br" target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>pagseguro.uol.com.br</a>). Para ambiente de homologação, use o Sandbox (<a href="https://sandbox.pagseguro.uol.com.br" target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>sandbox.pagseguro.uol.com.br</a>).
                </div>
              </div>

              <div style={{ background: '#fff', padding: '14px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>2. Gerar Token da API</div>
                <div>
                  Vá até o menu <strong>Vendas &gt; Integrações</strong> (ou <strong>Configurações &gt; Chaves de API</strong>). Clique em <strong>Gerar Token</strong> (Bearer Token) e copie o código gerado.
                </div>
              </div>

              <div style={{ background: '#fff', padding: '14px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>3. Cadastrar o Webhook</div>
                <div>
                  No PagBank, acesse a aba <strong>Notificações / Webhooks</strong>, adicione uma nova URL de notificação colando o link copiado no campo acima e ative os eventos de <strong>Cobranças / PIX</strong>.
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1rem', padding: '10px 14px', background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe', fontSize: '0.825rem', color: '#1e40af' }}>
              💡 <strong>Configuração Alternativa via Arquivo:</strong> Você também pode definir as credenciais diretamente no arquivo <code>backend/.env</code> com as variáveis <code>PAGSEGURO_TOKEN=seu_token</code>, <code>PAGSEGURO_SANDBOX=false</code> e <code>PAGSEGURO_WEBHOOK_SECRET=seu_segredo</code>. As configurações salvas nesta tela do painel administrativo têm prioridade imediata sobre o arquivo <code>.env</code>.
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: PLAN FORM                                                    */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {planModal.open && (
        <div className="adm-modal-overlay" onClick={() => setPlanModal({ open: false, plan: null })}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="adm-modal-header">
              <h3>{planModal.plan ? 'Editar Plano' : `Novo Plano — ${currentSystem.title}`}</h3>
              <button className="adm-modal-close" onClick={() => setPlanModal({ open: false, plan: null })}>✕</button>
            </div>
            <form onSubmit={handleSavePlan}>
              <div className="adm-modal-body">
                <div className="adm-form-grid">
                  <div className="adm-form-group full-width">
                    <label>Nome do Plano *</label>
                    <input name="name" defaultValue={planModal.plan?.name || ''} placeholder="Ex: Plano Frota Pro" required />
                  </div>

                  <div className="adm-form-group full-width">
                    <label>Descrição</label>
                    <input name="description" defaultValue={planModal.plan?.description || ''} placeholder="Breve resumo dos benefícios do plano" />
                  </div>

                  <div className="adm-form-group">
                    <label>Valor (R$) *</label>
                    <input name="price" type="number" step="0.01" min="0" defaultValue={planModal.plan?.price ?? ''} placeholder="Ex: 99.90" required />
                  </div>

                  <div className="adm-form-group">
                    <label>Ciclo de Cobrança *</label>
                    <select name="billing_cycle" defaultValue={planModal.plan?.billing_cycle || 'monthly'}>
                      <option value="monthly">Mensal</option>
                      <option value="quarterly">Trimestral</option>
                      <option value="semi_annual">Semestral</option>
                      <option value="annual">Anual</option>
                      <option value="custom">Personalizado</option>
                    </select>
                  </div>

                  <div className="adm-form-group">
                    <label>Limite de Usuários (em branco = ilimitado)</label>
                    <input name="max_users" type="number" min="1" defaultValue={planModal.plan?.max_users || ''} placeholder="Ilimitado" />
                  </div>

                  {currentSystem.id === 'task' && (
                    <div className="adm-form-group">
                      <label>Limite de Quadros (em branco = ilimitado)</label>
                      <input name="max_boards" type="number" min="1" defaultValue={planModal.plan?.max_boards || ''} placeholder="Ilimitado" />
                    </div>
                  )}

                  <div className="adm-form-group" style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', marginTop: '1.2rem' }}>
                      <input type="checkbox" name="is_active" defaultChecked={planModal.plan?.is_active ?? true} />
                      Plano Ativo para Vendas
                    </label>
                  </div>

                  <div className="adm-form-group full-width">
                    <label>Recursos Inclusos (um por linha)</label>
                    <textarea
                      name="features"
                      rows={4}
                      defaultValue={(planModal.plan?.features || []).join('\n')}
                      placeholder="Exemplo:&#10;Gestão completa de viagens&#10;Relatório de abastecimento&#10;Suporte prioritário"
                    />
                  </div>
                </div>
              </div>
              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-secondary" onClick={() => setPlanModal({ open: false, plan: null })}>
                  Cancelar
                </button>
                <button type="submit" className="adm-btn adm-btn-primary" style={{ background: currentSystem.color }}>
                  Salvar Plano
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: CLIENT LIBERATION FORM                                       */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {clientModal.open && (
        <div className="adm-modal-overlay" onClick={() => setClientModal({ open: false, subscription: null })}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div className="adm-modal-header">
              <h3>{clientModal.subscription ? 'Editar Liberação de Acesso' : `Liberar Acesso — ${currentSystem.title}`}</h3>
              <button className="adm-modal-close" onClick={() => setClientModal({ open: false, subscription: null })}>✕</button>
            </div>
            <form onSubmit={handleSaveClient}>
              <div className="adm-modal-body">
                <div className="adm-form-grid">
                  <div className="adm-form-group full-width">
                    <label>Selecione a Empresa (Cliente) *</label>
                    <select
                      name="company_id"
                      defaultValue={clientModal.subscription?.company_id || ''}
                      disabled={!!clientModal.subscription}
                      required
                    >
                      <option value="">-- Escolha uma empresa --</option>
                      {allCompanies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.document_number ? `(${c.document_number})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="adm-form-group full-width">
                    <label>Plano Vinculado</label>
                    <select
                      name="plan_id"
                      defaultValue={clientModal.subscription?.plan_id || ''}
                    >
                      <option value="">-- Sem plano fixo / Acesso avulso --</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {formatCurrency(p.price)} ({BILLING_CYCLES[p.billing_cycle] || p.billing_cycle})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="adm-form-group full-width" style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 8 }}>
                    <label style={{ fontWeight: 700, marginBottom: '0.4rem', display: 'block' }}>
                      Tipo de Liberação de Acesso *
                    </label>
                    <div style={{ display: 'flex', gap: '1.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="access_type"
                          value="integral"
                          defaultChecked={clientModal.subscription?.access_type === 'integral'}
                          id="access_type_integral"
                        />
                        <strong>♾️ Tempo Integral</strong> (permanente / vitalício)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="access_type"
                          value="temporary"
                          defaultChecked={clientModal.subscription?.access_type !== 'integral'}
                          id="access_type_temp"
                        />
                        <strong>⏱️ Tempo Determinado</strong> (com validade)
                      </label>
                    </div>
                  </div>

                  <div className="adm-form-group">
                    <label>Início da Liberação</label>
                    <input
                      name="starts_at"
                      type="date"
                      defaultValue={clientModal.subscription?.starts_at ? clientModal.subscription.starts_at.substring(0, 10) : toLocalDateInput(new Date())}
                    />
                  </div>

                  <div className="adm-form-group">
                    <label>Data de Expiração (se tempo determinado)</label>
                    <input
                      name="expires_at"
                      type="date"
                      defaultValue={clientModal.subscription?.expires_at ? clientModal.subscription.expires_at.substring(0, 10) : ''}
                    />
                  </div>

                  <div className="adm-form-group">
                    <label>Status do Acesso</label>
                    <select name="status" defaultValue={clientModal.subscription?.status || 'active'}>
                      <option value="active">Ativo (Liberado)</option>
                      <option value="trial">Período de Testes (Trial)</option>
                      <option value="suspended">Suspenso (Bloqueado)</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>

                  <div className="adm-form-group">
                    <label>Valor Cobrado (R$)</label>
                    <input
                      name="price"
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={clientModal.subscription?.price ?? ''}
                      placeholder="Ex: 150.00"
                    />
                  </div>

                  <div className="adm-form-group">
                    <label>Limite de Usuários (em branco = padrão/ilimitado)</label>
                    <input
                      name="max_users"
                      type="number"
                      min="1"
                      defaultValue={clientModal.subscription?.max_users ?? ''}
                      placeholder="Padrão do plano"
                    />
                  </div>

                  {currentSystem.id === 'task' && (
                    <div className="adm-form-group">
                      <label>Limite de Quadros (em branco = padrão/ilimitado)</label>
                      <input
                        name="max_boards"
                        type="number"
                        min="1"
                        defaultValue={clientModal.subscription?.max_boards ?? ''}
                        placeholder="Padrão do plano"
                      />
                    </div>
                  )}

                  {!clientModal.subscription && (
                    <div className="adm-form-group full-width" style={{ background: '#f0fdf4', padding: '0.75rem', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600 }}>
                        <input type="checkbox" name="create_first_installment" defaultChecked={true} />
                        Gerar primeira parcela de cobrança para esta liberação
                      </label>
                      <div style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.8rem', color: '#166534' }}>Vencimento da parcela:</span>
                        <input
                          name="installment_due_date"
                          type="date"
                          defaultValue={toLocalDateInput(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000))}
                          style={{ maxWidth: 160, padding: '0.25rem 0.5rem' }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="adm-form-group full-width">
                    <label>Notas / Observações</label>
                    <textarea name="notes" rows={2} defaultValue={clientModal.subscription?.notes || ''} placeholder="Anotações internas sobre esta liberação..." />
                  </div>
                </div>
              </div>
              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-secondary" onClick={() => setClientModal({ open: false, subscription: null })}>
                  Cancelar
                </button>
                <button type="submit" className="adm-btn adm-btn-primary" style={{ background: currentSystem.color }}>
                  Salvar Liberação
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: RENEW CLIENT                                                 */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {renewModal.open && (
        <div className="adm-modal-overlay" onClick={() => setRenewModal({ open: false, subscription: null })}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="adm-modal-header">
              <h3>Renovar / Estender Acesso — {renewModal.subscription?.company?.name}</h3>
              <button className="adm-modal-close" onClick={() => setRenewModal({ open: false, subscription: null })}>✕</button>
            </div>
            <form onSubmit={handleRenewClient}>
              <div className="adm-modal-body">
                <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Expiração Atual:</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>
                    {formatDate(renewModal.subscription?.expires_at)}
                  </div>
                </div>

                <div className="adm-form-group full-width">
                  <label>Adicionar Dias de Acesso:</label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    {[30, 60, 90, 365].map((days) => (
                      <button
                        key={days}
                        type="button"
                        className="adm-btn adm-btn-secondary adm-btn-sm"
                        onClick={() => {
                          const input = document.getElementById('days_to_add_input');
                          if (input) input.value = days;
                        }}
                      >
                        +{days} dias
                      </button>
                    ))}
                  </div>
                  <input id="days_to_add_input" name="days_to_add" type="number" min="1" defaultValue="30" />
                </div>

                <div className="adm-form-group full-width">
                  <label>Ou Definir Nova Data de Expiração Específica:</label>
                  <input name="new_expires_at" type="date" />
                </div>

                <div className="adm-form-group full-width" style={{ background: '#f0fdf4', padding: '0.75rem', borderRadius: 8, border: '1px solid #bbf7d0', marginTop: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600 }}>
                    <input type="checkbox" name="create_installment" defaultChecked={true} />
                    Gerar nova parcela / fatura para esta renovação
                  </label>
                  <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: '#166534' }}>Valor (R$):</span>
                      <input
                        name="amount"
                        type="number"
                        step="0.01"
                        defaultValue={renewModal.subscription?.price || ''}
                        style={{ padding: '0.25rem 0.5rem' }}
                      />
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: '#166534' }}>Vencimento:</span>
                      <input
                        name="due_date"
                        type="date"
                        defaultValue={toLocalDateInput(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000))}
                        style={{ padding: '0.25rem 0.5rem' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-secondary" onClick={() => setRenewModal({ open: false, subscription: null })}>
                  Cancelar
                </button>
                <button type="submit" className="adm-btn adm-btn-success">
                  Confirmar Renovação
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: PAYMENT / INSTALLMENT FORM                                   */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {paymentModal.open && (
        <div className="adm-modal-overlay" onClick={() => setPaymentModal({ open: false, payment: null })}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="adm-modal-header">
              <h3>{paymentModal.payment ? 'Editar Parcela' : `Nova Parcela — ${currentSystem.title}`}</h3>
              <button className="adm-modal-close" onClick={() => setPaymentModal({ open: false, payment: null })}>✕</button>
            </div>
            <form onSubmit={handleSavePayment}>
              <div className="adm-modal-body">
                <div className="adm-form-grid">
                  <div className="adm-form-group full-width">
                    <label>Cliente / Empresa *</label>
                    <select
                      name="company_id"
                      defaultValue={paymentModal.payment?.company_id || ''}
                      disabled={!!paymentModal.payment}
                      required
                    >
                      <option value="">-- Selecione a empresa --</option>
                      {allCompanies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.document_number ? `(${c.document_number})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="adm-form-group">
                    <label>Valor da Parcela (R$) *</label>
                    <input
                      name="amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      defaultValue={paymentModal.payment?.amount || ''}
                      placeholder="Ex: 199.90"
                      required
                    />
                  </div>

                  <div className="adm-form-group">
                    <label>Data de Vencimento *</label>
                    <input
                      name="due_date"
                      type="date"
                      defaultValue={paymentModal.payment?.due_date ? paymentModal.payment.due_date.substring(0, 10) : toLocalDateInput(new Date())}
                      required
                    />
                  </div>

                  <div className="adm-form-group">
                    <label>Referência (Mês/Ano)</label>
                    <input
                      name="reference_month"
                      type="text"
                      defaultValue={paymentModal.payment?.reference_month || new Date().toISOString().substring(0, 7)}
                      placeholder="Ex: 2026-09"
                    />
                  </div>

                  <div className="adm-form-group">
                    <label>Status</label>
                    <select name="status" defaultValue={paymentModal.payment?.status || 'pending'}>
                      <option value="pending">Pendente</option>
                      <option value="paid">Pago</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>

                  <div className="adm-form-group full-width">
                    <label>Observações</label>
                    <textarea
                      name="notes"
                      rows={2}
                      defaultValue={paymentModal.payment?.notes || ''}
                      placeholder="Ex: Mensalidade ref. Setembro/2026"
                    />
                  </div>
                </div>
              </div>
              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-secondary" onClick={() => setPaymentModal({ open: false, payment: null })}>
                  Cancelar
                </button>
                <button type="submit" className="adm-btn adm-btn-primary" style={{ background: currentSystem.color }}>
                  Salvar Parcela
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: CHECKOUT PIX COM PAGSEGURO                                  */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {pixModal.open && pixModal.payment && (
        <div className="adm-modal-overlay" onClick={() => setPixModal({ open: false, payment: null, pixData: null })}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540, maxHeight: '90vh', overflowY: 'auto', textAlign: 'center' }}>
            <div className="adm-modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center' }}>
                <span>⚡</span> Checkout PIX — PagSeguro
              </h3>
              <button className="adm-modal-close" onClick={() => setPixModal({ open: false, payment: null, pixData: null })}>✕</button>
            </div>

            <div className="adm-modal-body" style={{ textAlign: 'center' }}>
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: 8, marginBottom: '1rem', textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Cliente:</span>
                  <strong>{pixModal.payment.company?.name || 'Cliente'}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Referência:</span>
                  <span style={{ fontFamily: 'monospace' }}>{pixModal.payment.reference_month || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Valor a Pagar:</span>
                  <strong style={{ fontSize: '1.25rem', color: '#059669' }}>
                    {formatCurrency(pixModal.payment.amount)}
                  </strong>
                </div>
              </div>

              {pixModal.pixData ? (
                <div>
                  {pixModal.pixData.is_contingency && (
                    <div style={{
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderLeft: '4px solid #f59e0b',
                      borderRadius: 6,
                      padding: '0.65rem 0.85rem',
                      marginBottom: '0.85rem',
                      textAlign: 'left',
                      fontSize: '0.82rem',
                      color: '#92400e',
                    }}>
                      <strong style={{ display: 'block', marginBottom: 2 }}>⚡ Recebimento via Chave PIX Direta (Banco Central)</strong>
                      Sua conta PagBank em produção requer liberação de Whitelist pelo suporte PagSeguro para API dinâmica. Este QR Code foi gerado diretamente com sua Chave PIX oficial (BACEN) e funciona perfeitamente em qualquer aplicativo de banco!
                    </div>
                  )}

                  {/* QR Code Container */}
                  <div style={{ margin: '0.5rem auto 1rem', padding: '0.75rem', background: '#fff', border: '2px dashed #0284c7', borderRadius: 12, display: 'inline-block' }}>
                    <PixQrCode
                      text={pixModal.pixData.copy_paste}
                      fallbackUrl={pixModal.pixData.qr_code_url}
                    />
                  </div>

                  <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0.4rem 0' }}>
                    Abra o aplicativo do seu banco e escaneie a imagem acima ou copie o código PIX abaixo:
                  </p>

                  {/* PIX Copia e Cola */}
                  <div style={{ margin: '0.6rem 0' }}>
                    <textarea
                      readOnly
                      value={pixModal.pixData.copy_paste || ''}
                      rows={3}
                      style={{
                        width: '100%',
                        fontSize: '0.78rem',
                        fontFamily: 'monospace',
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        borderRadius: 6,
                        padding: '0.5rem',
                        resize: 'none',
                        wordBreak: 'break-all',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <button
                      className="adm-btn adm-btn-primary"
                      onClick={handleCopyPixCode}
                      style={{ background: pixModal.copied ? '#059669' : '#0284c7', borderColor: pixModal.copied ? '#059669' : '#0284c7' }}
                    >
                      {pixModal.copied ? '✓ Código PIX Copiado!' : '📋 Copiar Código PIX (Copia e Cola)'}
                    </button>
                    <button
                      className="adm-btn adm-btn-secondary"
                      onClick={handleRegeneratePix}
                      title="Regenerar QR Code com os dados mais recentes"
                    >
                      🔄 Novo QR Code
                    </button>
                  </div>

                  {/* Verification & Manual Confirmation */}
                  <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      className="adm-btn adm-btn-secondary"
                      onClick={handleCheckPixStatus}
                      disabled={pixModal.checking}
                    >
                      {pixModal.checking ? 'Consultando...' : '🔄 Consultar Status no PagSeguro'}
                    </button>

                    <button
                      className="adm-btn adm-btn-success"
                      onClick={() => handleMarkPaid(pixModal.payment)}
                    >
                      ✓ Confirmar Pagamento Manual
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '2rem 0' }}>
                  <div className="adm-loading">Gerando cobrança PIX no PagSeguro...</div>
                </div>
              )}
            </div>

            <div className="adm-modal-footer" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="adm-btn adm-btn-secondary"
                onClick={() => setPixModal({ open: false, payment: null, pixData: null })}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
